import { company } from '~/company/Company';

import type { CloudflareResizeOptions } from './CloudflareResizeOptions';

const fitToResizeType: Record<string, string> = {
  'scale-down': 'fit',
  contain: 'fit',
  cover: 'fill',
  crop: 'fill',
  pad: 'fit',
  auto: 'auto',
};

/**
 * Cloudflare's gravity names translated to imgproxy's compass codes. The option
 * is spelled in Cloudflare terms across the codebase, and imgproxy answers an
 * unknown code with a 404 rather than ignoring it — `g:top` returns no image at
 * all — so an unmapped value must be dropped rather than passed through.
 */
const gravityToImgproxy: Record<string, string> = {
  auto: 'sm',
  top: 'no',
  bottom: 'so',
  left: 'we',
  right: 'ea',
};

export const removeSlash = (src: string) =>
  src.startsWith('/') ? src.slice(1) : src;

/**
 * CDN host for the active tenant.
 *
 * Resolved per call rather than hoisted: on the server `company` is assigned per
 * request, so a module-level value would freeze on whichever tenant loaded the
 * module first.
 */
export const getCloudflareDomain = (): string =>
  company.match({
    citydrinks:
      company.envVariables.REACT_APP_CLOUDFLARE_IMAGE_RESIZE_DOMAIN_OLD,
    default: company.envVariables.REACT_APP_CLOUDFLARE_IMAGE_RESIZE_DOMAIN,
  });

const getImgproxyUrl = (
  src: string,
  domain: string,
  options: CloudflareResizeOptions,
): string => {
  const processingOptions: string[] = [];

  const resizeType = options.fit
    ? (fitToResizeType[options.fit] ?? 'fit')
    : 'fit';
  const width = options.width ?? 0;
  const height = options.height ?? 0;
  processingOptions.push(`rs:${resizeType}:${width}:${height}`);

  if (options.dpr) {
    processingOptions.push(`dpr:${options.dpr}`);
  }

  if (options.quality) {
    processingOptions.push(`q:${options.quality}`);
  }

  if (options.blur) {
    processingOptions.push(`bl:${options.blur}`);
  }

  if (options.sharpen) {
    processingOptions.push(`sh:${options.sharpen}`);
  }

  if (options.gravity) {
    const gravity = gravityToImgproxy[options.gravity];

    if (gravity) {
      processingOptions.push(`g:${gravity}`);
    }
  }

  if (options.rotate) {
    processingOptions.push(`rot:${options.rotate}`);
  }

  if (options.brightness) {
    processingOptions.push(`br:${options.brightness}`);
  }

  if (options.contrast) {
    processingOptions.push(`co:${options.contrast}`);
  }

  if (options.background) {
    processingOptions.push(`bg:${options.background}`);
  }

  return `${domain}/insecure/${processingOptions.join('/')}/plain/${removeSlash(src)}`;
};

/**
 * Builds a Cloudflare Images URL. Explicitly-undefined options (used by callers
 * to drop an inherited one) are skipped so they cannot reach the URL as the
 * literal string "undefined".
 *
 * Options are sorted by name so that the same request always produces the same
 * URL regardless of the key order of the object it came from. Cloudflare treats
 * the option list as an opaque cache key, so `width,height,fit` and
 * `fit,width,height` were two cache entries for one image — and the hero
 * preload `href` and its `imagesrcset` candidate disagreed for exactly that
 * reason.
 */
const getCloudflareImageUrl = (
  src: string,
  domain: string,
  options: CloudflareResizeOptions,
): string => {
  const params = Object.entries(options)
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value}`);

  params.push('format=auto');

  return `${domain}/cdn-cgi/image/${params.join(',')}/${removeSlash(src)}`;
};

/**
 * Encode quality applied when a caller does not ask for a specific one.
 *
 * Every tenant trades a little fidelity for LCP bytes, MCosmetic included.
 * It ran at 95 and then 100 for a while, on the argument that cosmetics is
 * judged on packaging gradients and shade swatches — reverted because the bytes
 * are not worth it: its home page measured 6.6 MB at 100 against 1.7 MB at 90,
 * and the two are indistinguishable side by side.
 *
 * 100 is only safe on the imgproxy path, which keeps encoding AVIF. Never set
 * it for a tenant on the Cloudflare path (CityDrinks): there `quality=100`
 * makes Cloudflare drop `format=auto` and serve the untouched source JPEG —
 * measured at 2.9 MB for a 3840px hero versus 356 KB of WebP at 80.
 */
const DEFAULT_QUALITY = 90;

export const getCloudflareUrl = (
  src: string,
  domain?: string,
  options?: CloudflareResizeOptions,
): string => {
  if (!options) {
    return src;
  }

  const withQuality: CloudflareResizeOptions =
    options.quality === undefined
      ? { ...options, quality: DEFAULT_QUALITY }
      : options;

  const buildUrl = company.match({
    citydrinks: getCloudflareImageUrl,
    default: getImgproxyUrl,
  });

  return buildUrl(src, domain ?? '', withQuality);
};

/**
 * Widest candidate worth emitting. Past this the descriptor only exists for a
 * combination that barely occurs — a 3x screen on a desktop-sized viewport —
 * while costing a multiple of the bytes on the largest assets we serve. It also
 * keeps the desktop hero, whose base width is already 1920, from advertising a
 * 5760px variant.
 */
const MAX_SRCSET_WIDTH = 3840;

/**
 * Builds a DPR-based `srcset` ("<url> 1x, <url> 2x") for a fixed-width image.
 * Returns an empty string when there is no width.
 *
 * Density stops at 2x unless the tenant opts in via
 * `company.highDensityImagesEnabled`, which is where the trade-off is argued.
 * Resolved per call rather than hoisted: on the server `company` is assigned
 * per request, so a module-level value would freeze on whichever tenant loaded
 * the module first.
 *
 * Callers may still pass `maxDpr` explicitly to pin a range regardless of the
 * tenant flag.
 *
 * `height` is forwarded only alongside a cropping `fit`, where it defines the
 * crop the layout reserves space for (hero banners). Under `contain` a height
 * would instead shrink the result — a portrait source bounded on both sides
 * comes back at half the requested width — which costs resolution in every
 * caller that pairs a square request with a CSS `object-fit: cover` box.
 * `dpr` is dropped because the density is already baked into the pixel size
 * requested per descriptor; keeping it would apply the scale twice.
 */
export const buildDprSrcSet = (
  src: string,
  domain: string,
  params: CloudflareResizeOptions,
  maxDpr?: number,
): string => {
  if (!params.width) {
    return '';
  }

  const densityCap = maxDpr ?? (company.highDensityImagesEnabled ? 3 : 2);

  const cropsToBox = params.fit === 'cover' || params.fit === 'crop';
  const scaledHeight = cropsToBox && params.height ? params.height : undefined;

  const descriptors: string[] = [];
  for (let dpr = 1; dpr <= densityCap; dpr++) {
    const width = params.width * dpr;
    if (dpr > 1 && width > MAX_SRCSET_WIDTH) {
      break;
    }
    const url = getCloudflareUrl(src, domain, {
      fit: 'contain',
      ...params,
      dpr: undefined,
      width,
      height: scaledHeight ? scaledHeight * dpr : undefined,
    });
    descriptors.push(`${url} ${dpr}x`);
  }

  return descriptors.join(', ');
};
